

























































































































































































































































































































































































































import {
  computed,
  defineComponent,
  useContext,
  useRoute,
  onMounted,
  onUnmounted,
  ref,
  nextTick,
  watch
} from '@nuxtjs/composition-api';
import { mapGetters } from 'vuex';
import type { ImageGalleryContent } from '~/components/molecules/ImageGallery.vue';

import {
  onSSR,
  sharedRef,
  useVSFContext,
  VSF_CURRENCY_COOKIE,
  Logger
} from '@vue-storefront/core';
import {
  usePDP,
  useTranslations,
  useAlgoliaInsights
} from '@vue-storefront/novulo';
import { CacheTagPrefix } from '@vue-storefront/cache';
import useUrlFormatter from '~/hooks/useUrlFormatter';
import useDefaultHeaderToggle from '~/hooks/useDefaultHeaderToggle';
import {
  getMetaTags,
  getSSRRequestFullUrl,
  getSSRRequestWebsite,
  getVerboliaScriptSrc,
  getStatusReferenceEnum,
  StatusReference,
  getCurrencyCode,
  priceFormat,
  addCurrency
} from '~/helpers/utils';
import useBreadcrumbsStructuredData from '~/hooks/useBreadcrumbsStructuredData';
import useWindowResize from '~/hooks/useResizeWindow';
import usePaypalMessaging from '~/hooks/usePaypalMessaging';

export default defineComponent({
  props: {
    urlInfo: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const route = useRoute();
    const productSlug = route.value.params.slug;
    const initializedGTM = false;
    const { i18n, error: nuxtError, res, $config } = useContext();
    const isMainMenuOpen = sharedRef(false, 'main-menu-open');
    const vsfContext = useVSFContext();
    const { translate } = useTranslations();
    const formatUrl = useUrlFormatter();
    const { $novulo } = useVSFContext();
    const wt = $novulo.config.state.getWt();
    const websiteCountry =
      $novulo.config.state.domainConfig.websiteCountry.toUpperCase();
    const { trackAddToCartPDP, ALGOLIA_PRODUCTS_CLICKED, ALGOLIA_PRODUCTS_ADDED, addToSessionStorage, fromSessionStorage, removeFromSessionStorage } = useAlgoliaInsights();
    const { toggleHideHeader } = useDefaultHeaderToggle();
    const { isDesktop } = useWindowResize();
    const TRUSTEDSHOPS_BODY_CLASS = 'pdp-hide-trustedshops';
    const PDP_FOOTER_OFFSET_CLASS = 'pdp-footer-offset';
    const { isPaypalMessagingEnabled, isPaypalSdkReady } =
      usePaypalMessaging();

    const PAYPAL_NAMESPACE = 'PayPalSDK';
    const PAYPAL_MESSAGE_ID = 'paypal-paylater-message-pdp';

    const {
      getPDP,
      loading,
      getPDPError,
      pdpResult,
      specifications,
      hasLoadedSpecifications
    } = usePDP(productSlug);

    const { relatedAcc, hasLoadedRelatedAcc } = usePDP(productSlug);

    const showTips = computed(() => {
      return pdpResult.value?.inspiration?.filter((insp) => insp.description).length > 0;
    });

    const productPromotionsTitle = computed(() => {
      return translate('product_promotions', {
        productName: pdpResult.value?.name || 'product'
      });
    });

    const productStatusReference = computed(() => {
      return getStatusReferenceEnum(pdpResult.value?.status_reference || 0);
    });

    // answerbase
    const isMounted = ref(false);
    const hasProduct = computed(() => Boolean(pdpResult.value?.product_id));
    const isDE = [21].includes(wt);
    const isNL = [11].includes(wt);

    const showAnswerBase = computed(() =>
      (isDE || isNL) &&
      isMounted.value &&
      hasProduct.value &&
      productStatusReference.value !== StatusReference.ON_ORDER &&
      pdpResult.value?.is_occasion !== true &&
      pdpResult.value?.is_second_hand_product !== true &&
      pdpResult.value?.end_of_life !== true &&
      pdpResult.value?.stock_classification_code !== 'not_deliverable'
    );

    const answerBaseId = isDE ? 'kameraexpress2' : 'kameraexpressnl';

    const inPageNavigation = computed(() => {
      const items = [
        {
          text: i18n.t('Combideals'),
          href: '#combidealsSection',
          condition: pdpResult.value?.combideals?.length > 0,
          apiCode: 'combideals'
        },
        {
          text: i18n.t('Accessories'),
          href: '#relatedAccessories',
          condition: hasLoadedRelatedAcc.value && relatedAcc.value?.length > 0,
          apiCode: 'accessories'
        },
        {
          text: i18n.t('Actions'),
          href: '#promotionsSection',
          condition: pdpResult.value?.promotions?.length > 0,
          apiCode: 'actions'
        },
        {
          text: i18n.t('Reviews'),
          href: '#reviewsSection',
          condition: !pdpResult.value?.is_occasion,
          apiCode: 'reviews'
        },
        {
          text: i18n.t('Description'),
          href: '#descriptionSection',
          condition: pdpResult.value?.detailed_description,
          apiCode: 'description'
        },
        {
          text: i18n.t('Tips'),
          href: '#tipsSection',
          condition: showTips.value,
          apiCode: 'tips'
        },
        {
          text: i18n.t('Specifications'),
          href: '#specificationsSection',
          condition: true,
          apiCode: 'specifications'
        },
        {
          text: translate('pdp_faq'),
          href: '#answerBaseSection',
          condition: showAnswerBase.value,
          apiCode: 'answerbase'
        }
      ];
      const apiUrls = pdpResult.value?.quicklinks?.[0]?.urls || [];

      return items.filter(
        (item) =>
          item.condition &&
          apiUrls.some((url) => url.code_english === item.apiCode)
      );
    });

    const sortedInPageNavigation = computed(() => {
      const apiUrls = pdpResult.value?.quicklinks?.[0]?.urls || [];
      return [...inPageNavigation.value].sort((a, b) => {
        const aIndex = apiUrls.findIndex(
          (url) => url.code_english === a.apiCode
        );
        const bIndex = apiUrls.findIndex(
          (url) => url.code_english === b.apiCode
        );
        return aIndex - bIndex;
      });
    });

    const breadcrumbs = computed(() => {
      const breadcrumbsData = pdpResult.value.breadcrumb;
      if (!breadcrumbsData || breadcrumbsData.length === 0) return [];
      return [
        {
          text: i18n.t('breadcrumbs_home'),
          link: formatUrl('/'),
          testId: i18n.t('breadcrumbs_home')
        },
        {
          text: breadcrumbsData[0].parenttaggroup,
          link: formatUrl(breadcrumbsData[0].parenttaggroup_url),
          testId: breadcrumbsData[0].category_level_1_nl
        },
        {
          text: breadcrumbsData[0].producttaggroup,
          link: formatUrl(breadcrumbsData[0].producttaggroup_url),
          testId: breadcrumbsData[0].category_level_2_nl
        },
        {
          text: breadcrumbsData[0].primary_tag,
          link: formatUrl(breadcrumbsData[0].producttag_url),
          testId: breadcrumbsData[0].category_level_3_nl
        },
        {
          text: breadcrumbsData[0].product_name,
          link: '#',
          testId: breadcrumbsData[0].title_nl
        }
      ];
    });

    const additionalServices = computed(() => {
      if (!pdpResult.value) return [];
      const secondHand = pdpResult.value.second_hand;
      const leasing = pdpResult.value.leasing;
      const rental = pdpResult.value.rental_product;
      const services = [];
      if (secondHand && secondHand.scp_id) {
        const originalPrice = pdpResult.value.price;
        const discountPrice = pdpResult.value.price_after_cash_discount;
        const currentPrice =
          discountPrice && discountPrice < originalPrice
            ? discountPrice
            : originalPrice;
        services.push({
          thumbnail: secondHand.media,
          alt: secondHand.media_file_name,
          type: i18n.t('second_hand'),
          description: i18n.t('checked_by_our_experts'),
          isGreen: true,
          price: secondHand.price,
          priceDifference: currentPrice - secondHand.price,
          link: formatUrl(secondHand.url),
          target: '_self'
        });
      }
      if (leasing && leasing.description) {
        services.push({
          thumbnail:
            pdpResult.value?.product_image_gallery?.[0]?.media_url || '',
          alt: pdpResult.value?.product_image_gallery?.[0]?.media_file_name,
          type: translate('pdp_lease_title'),
          isGreen: false,
          ctaText: i18n.t('more_info_about_business_leasing'),
          link: formatUrl(leasing.url),
          target: '_self'
        });
      }
      if (rental && rental.scp_id) {
        services.push({
          thumbnail: rental.media,
          alt: rental.media_file_name,
          type: translate('pdp_rental_title'),
          description: translate('price_rental_day_price'),
          isGreen: true,
          price: rental.price,
          link: formatUrl(rental.url),
          target: '_blank'
        });
      }
      return services;
    });

    const promotions = computed(() => {
      if (!pdpResult.value) return [];

      const promotions =
        pdpResult.value.promotions?.filter((item) => !item.is_service) || [];

      return promotions;
    });

    const services = computed(() => {
      if (!pdpResult.value) return [];

      const services = pdpResult.value.promotions?.filter(
        (item) => item.is_service
      );

      return services;
    });

    const showPromotionsAndServices = computed(() => {
      return promotions.value.length || services.value.length;
    });

    const buySectionRef = ref<HTMLElement | null>(null);
    const originalAtcButton = ref<HTMLButtonElement | null>(null);
    const isAtcInView = ref(false);
    const isStickyAtcDisabled = ref(false);
    const isStickyFooterReady = ref(false);
    let stickyAtcObserver: IntersectionObserver | null = null;
    let atcButtonMutationObserver: MutationObserver | null = null;

    const isStickyFooterMobile = computed(() => !isDesktop.value);

    const stickyCurrentPrice = computed(() => {
      const currentPrice = pdpResult.value?.price || 0;
      const cashDiscountPrice = pdpResult.value?.price_after_cash_discount || 0;
      return cashDiscountPrice && cashDiscountPrice < currentPrice
        ? cashDiscountPrice
        : currentPrice;
    });

    const stickyCashbackPrice = computed(() => {
      return (
        pdpResult.value?.price_after_cashback_discount ||
        pdpResult.value?.price_after_cashback ||
        0
      );
    });

    const showStickyCashback = computed(() => {
      if (!stickyCashbackPrice.value) return false;
      return stickyCashbackPrice.value < stickyCurrentPrice.value;
    });

    const formattedStickyCurrentPrice = computed(() => {
      if (!stickyCurrentPrice.value) return '';
      return addCurrency(priceFormat(stickyCurrentPrice.value));
    });

    const stickyCashbackText = computed(() => {
      if (!showStickyCashback.value) return '';
      return `${translate('price_after_cashback')} ${addCurrency(
        priceFormat(stickyCashbackPrice.value)
      )}`;
    });

    const showStickyFooter = computed(() => {
      return (
        isStickyFooterMobile.value &&
        isStickyFooterReady.value &&
        !isAtcInView.value &&
        !isMainMenuOpen.value
      );
    });

    const buySectionUsps = computed(() => {
      const usps = pdpResult.value?.usps;
      if (!usps || !usps.length) return [];
      const filter =
        productStatusReference.value === StatusReference.IN_STOCK
          ? 'in_stock'
          : 'out_of_stock';
      const statusRelevantUsps = usps.find((usp) => usp.code === filter);
      return statusRelevantUsps?.urls || [];
    });

    const paypalAmount = computed(() => {
      const total = pdpResult.value?.price;
      return typeof total === 'number' ? total.toFixed(2) : null;
    });

    const renderPayPalMessage = async () => {
      if (process.server) return;
      if (!isPaypalSdkReady.value) return;
      if (!isPaypalMessagingEnabled.value) return;
      if (!paypalAmount.value) return;

      try {
        const sdk = (window as any)[PAYPAL_NAMESPACE];
        if (!sdk?.Messages) return;

        const target = `#${PAYPAL_MESSAGE_ID}`;
        await nextTick();
        if (!document.querySelector(target)) return;
        await sdk.Messages({
          amount: paypalAmount.value,
          buyerCountry: websiteCountry,
          placement: 'product',
          style: {
            layout: 'text',
            logo: { type: 'inline', position: 'left' },
            text: { color: 'black', size: '16', fontFamily: 'Barlow' }
          }
        }).render(target);
      } catch (error) {
        console.error('[PayPal Message] Render failed', error);
      }
    };

    if (process.client) {
      watch(paypalAmount, () => {
        renderPayPalMessage();
      });

      watch(
        () => isPaypalSdkReady.value,
        (isReady) => {
          if (isReady) {
            renderPayPalMessage();
          }
        }
      );
    }

    onSSR(async () => {
      if (route.value.params.slug) {
        await getPDP(props.urlInfo.spli_id, props.urlInfo.product_id).catch(
          (err) => {
            Logger.error(err);
            throw err;
          }
        );
      }

      if (!pdpResult.value) {
        Logger.error('PDP empty response');
        nuxtError({ statusCode: 500 });
      }
      if (getPDPError.value.getPDP) nuxtError({ statusCode: 404 });

      // set gtm params
      if (pdpResult.value) {
        context.root.$store.dispatch('gtm-init/setExtraParams', {
          productId: pdpResult.value.product_id,
          brand: pdpResult.value.brand,
          category: pdpResult.value.category
        });
      }

      let tags = [];

      const addTags = (items, idAttribute) => {
        if (items?.length) {
          tags = [
            ...tags,
            ...items.map(
              (item) => `${CacheTagPrefix.Product}${item[idAttribute]}`
            )
          ];
        }
      };

      if (pdpResult.value?.product_id) {
        tags = [
          `${CacheTagPrefix.View}product`,
          `${CacheTagPrefix.Product}${pdpResult.value.product_id}`,
          `${CacheTagPrefix.Product}${pdpResult.value.second_hand?.product_id}`
        ];
      }

      addTags(pdpResult.value?.variations, 'product_id');
      addTags(pdpResult.value?.related_products, 'product_id');
      addTags(pdpResult.value?.combideals, '_id');

      res.setHeader('Cache-Tag', tags.join(','));
      res.setHeader(
        'Cache-Control',
        `max-age=${$config.cfBrowserCacheTimeout}, public`
      );
      res.setHeader(
        'Cloudflare-CDN-Cache-Control',
        `max-age=${$config.cfCacheTimeout}, public`
      );
    });

    const handleScrollToRef = (ref: string) => {
      const element = document.querySelector(ref);
      if (!element) return;

      toggleHideHeader(false);

      nextTick(() => {
        const docStyles = getComputedStyle(document.documentElement);
        const headerHeight =
          Number.parseInt(docStyles.getPropertyValue('--header-height-total')) ||
          Number.parseInt(docStyles.getPropertyValue('--header-height')) ||
          0;
        const stickyBar = document.querySelector('.sticky-bar-container') as HTMLElement | null;
        const stickyBarHeight = stickyBar?.offsetHeight || 0;
        const stickyBarOffset =
          stickyBar ? Number.parseInt(getComputedStyle(stickyBar).top || '0') || 0 : 0;
        const yOffset = -(stickyBarHeight + Math.max(headerHeight, stickyBarOffset));

        const scrollPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      });
    };

    const cleanupStickyFooterObservers = () => {
      if (stickyAtcObserver) {
        stickyAtcObserver.disconnect();
        stickyAtcObserver = null;
      }
      if (atcButtonMutationObserver) {
        atcButtonMutationObserver.disconnect();
        atcButtonMutationObserver = null;
      }
    };

    const initStickyFooterObserver = () => {
      if (!process.client) return;
      if (!buySectionRef.value) return;

      const atcButton = buySectionRef.value.querySelector(
        'button[data-dd-action-name="add-to-cart"]'
      ) as HTMLButtonElement | null;

      if (!atcButton) return;

      originalAtcButton.value = atcButton;
      isStickyFooterReady.value = true;
      isStickyAtcDisabled.value =
        atcButton.hasAttribute('disabled') ||
        atcButton.getAttribute('aria-disabled') === 'true';

      cleanupStickyFooterObservers();

      stickyAtcObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          isAtcInView.value = Boolean(entry && entry.isIntersecting);
        },
        { threshold: 0.1 }
      );

      stickyAtcObserver.observe(atcButton);

      atcButtonMutationObserver = new MutationObserver(() => {
        isStickyAtcDisabled.value =
          atcButton.hasAttribute('disabled') ||
          atcButton.getAttribute('aria-disabled') === 'true';
      });

      atcButtonMutationObserver.observe(atcButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
    };

    const handleStickyAddToCart = () => {
      if (!originalAtcButton.value || isStickyAtcDisabled.value) return;
      originalAtcButton.value.click();
    };

    function handleAddToCart(product) {
      const clickedProduct = fromSessionStorage(ALGOLIA_PRODUCTS_CLICKED, product.sku);

      if (clickedProduct) {
        addToSessionStorage(ALGOLIA_PRODUCTS_ADDED, product.sku, clickedProduct);
        removeFromSessionStorage(ALGOLIA_PRODUCTS_CLICKED, product.sku);
      }

      trackAddToCartPDP({
        objectID: String(product.sku),
        queryID: clickedProduct ?? null
      });
    }

    const setHeaderData = () => {
      const pdp = {
        image: pdpResult.value?.product_image_gallery[0]?.media_url,
        name: pdpResult.value?.name,
        description: pdpResult.value?.description,
        brand: pdpResult.value?.brand,
        ean: pdpResult.value?.ean,
        discountPrice: pdpResult.value?.price_after_cash_discount,
        price: pdpResult.value?.price,
        sku: pdpResult.value?.sku,
        salesPrice: pdpResult.value?.price_after_cash_discount,
        priceValidUntil: pdpResult.value?.pricevalid_until
      };

      const website = process.server
        ? getSSRRequestWebsite((context as any).ssrContext.req)
        : window.location.origin;

      const fullPath = process.server
        ? getSSRRequestFullUrl((context as any).ssrContext.req)
        : window.location.href;
      const canonicalUrl = fullPath.split('?')[0];

      const websiteCountry =
        vsfContext.$novulo.config.state.domainConfig.websiteCountry.toUpperCase();

      const productAvailabilitySchemas = {
        [StatusReference.IN_STOCK]: 'https://schema.org/InStock',
        [StatusReference.NOT_DELIVERABLE]: 'https://schema.org/Discontinued',
        [StatusReference.ON_ORDER]: 'https://schema.org/BackOrder',
        [StatusReference.TEMPORARILY_SOLD_OUT]: 'https://schema.org/OutOfStock',
        [StatusReference.PRE_ORDER]: 'https://schema.org/PreSale'
      };

      const itemCondition = pdpResult.value.is_occasion
        ? 'https://schema.org/UsedCondition'
        : 'https://schema.org/NewCondition';

      const currentPrice = pdp.discountPrice
        ? Math.min(pdp.discountPrice, pdp.price)
        : pdp.price;

      return {
        '@context': 'https://www.schema.org',
        '@type': 'Product',
        '@id': canonicalUrl,
        name: pdp.name,
        image: `${website}${pdp.image}`,
        url: canonicalUrl,
        description: pdp.description,
        brand: {
          '@type': 'Brand',
          name: pdp.brand
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: websiteCountry,
          returnPolicyCategory:
            'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 14,
          returnMethod: [
            'https://schema.org/ReturnByMail',
            'https://schema.org/ReturnInStore'
          ],
          returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
          returnPolicyCountry: websiteCountry
        },
        offers: {
          '@type': 'Offer',
          priceCurrency: getCurrencyCode({ vsfContext }),
          price: String(currentPrice),
          availability:
            productAvailabilitySchemas[productStatusReference.value] ||
            'https://schema.org/InStock',
          itemCondition,
          sku: pdp.sku,
          gtin13: pdp.ean
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          worstRating: 1,
          bestRating: 5,
          ratingValue: pdpResult.value.rating_number,
          reviewCount: pdpResult.value.total_reviews
        }
      };
    };

    const currentUrl = computed(() => (process.client ? window.location.href : ''));
    const imageUrl = computed(() => $config.mediaBaseURL + (pdpResult.value.media || ''));

    const updateTrustedshopsVisibility = () => {
      if (!process.client || !document?.body) return;

      document.body.classList.toggle(TRUSTEDSHOPS_BODY_CLASS, !isDesktop.value);
    };

    const updatePdpFooterOffsetClass = () => {
      if (!process.client || !document?.body) return;

      document.body.classList.toggle(PDP_FOOTER_OFFSET_CLASS, !isDesktop.value);
    };

    onMounted(() => {
      isMounted.value = true;
      updateTrustedshopsVisibility();
      updatePdpFooterOffsetClass();
      nextTick(() => {
        initStickyFooterObserver();
      });
      renderPayPalMessage();
    });

    onUnmounted(() => {
      if (process.client && document?.body) {
        document.body.classList.remove(TRUSTEDSHOPS_BODY_CLASS);
        document.body.classList.remove(PDP_FOOTER_OFFSET_CLASS);
      }
      cleanupStickyFooterObservers();
    });

    watch(isDesktop, () => {
      updateTrustedshopsVisibility();
      updatePdpFooterOffsetClass();
    });

    watch(
      () => pdpResult.value,
      () => {
        isStickyFooterReady.value = false;
        originalAtcButton.value = null;
        nextTick(() => {
          initStickyFooterObserver();
        });
      }
    );

    return {
      StatusReference,
      answerBaseId,
      additionalServices,
      breadcrumbs,
      buySectionRef,
      currentUrl,
      imageUrl,
      buySectionUsps,
      paypalAmount,
      paypalMessageId: PAYPAL_MESSAGE_ID,
      isPaypalMessagingEnabled,
      isPaypalSdkReady,
      formattedStickyCurrentPrice,
      handleScrollToRef,
      hasLoadedRelatedAcc,
      hasLoadedSpecifications,
      handleStickyAddToCart,
      inPageNavigation,
      initializedGTM,
      isStickyAtcDisabled,
      showStickyFooter,
      loading,
      locale: i18n.locale,
      pdpResult,
      productPromotionsTitle,
      productSlug,
      productStatusReference,
      promotions,
      relatedAcc,
      services,
      setHeaderData,
      showPromotionsAndServices,
      showStickyCashback,
      showTips,
      stickyCashbackText,
      stickyCurrentPrice,
      sortedInPageNavigation,
      specifications,
      showAnswerBase,
      translate,
      wt,
      isMounted,
      handleAddToCart
    };
  },
  computed: {
    ...mapGetters('page', ['getPageType'])
  },
  watch: {
    pdpResult(newVal) {
      if (newVal) this.addGTM();
    }
  },
  mounted() {
    if (!this.initializedGTM) this.addGTM();
  },
  head() {
    if (!this.pdpResult?.name) return {};

    const pageURL = process.server
      ? getSSRRequestFullUrl(this.$ssrContext.req)
      : window.location.href;

    const canonicalUrl = pageURL.split('?')[0];
    const verboliaScriptSrc = getVerboliaScriptSrc(pageURL, this.$i18n);

    // hreflang tags have to be set manually because the relative url changes per locale
    const hrefLangTags =
      this.pdpResult.cmswebsiteredirects?.map((redirect) => ({
        hid: `hreflang-${redirect.locale}`,
        rel: 'alternate',
        hreflang: redirect.locale,
        href: redirect.url
      })) || [];

    const pageTitle = this.urlInfo?.meta_title || this.pdpResult.name;

    const isPreorder =
      this.productStatusReference === StatusReference.PRE_ORDER;

    const metaTags = getMetaTags({
      title: pageTitle,
      description: this.urlInfo?.meta_description || '',
      image: this.mediaUrl(
        this.pdpResult.product_image_gallery?.[0]?.media_url
      ),
      url: pageURL,
      type: 'Product',
      locale: this.$i18n.locale
    });

    if (isPreorder) {
      metaTags.push({
        name: 'robots',
        content: 'noindex'
      });
    }

    const breadcrumbsStructuredData = useBreadcrumbsStructuredData(
      this.breadcrumbs,
      this.$ssrContext
    );

    return {
      title: pageTitle,
      __dangerouslyDisableSanitizers: ['script'],
      meta: metaTags,
      link: [
        {
          rel: 'canonical',
          href: canonicalUrl
        },
        ...hrefLangTags
      ],
      script: [
        {
          innerHTML: JSON.stringify(this.setHeaderData()),
          type: 'application/ld+json'
        },
        ...(verboliaScriptSrc
          ? [{
            hid: 'verbolia-script',
            type: 'text/javascript',
            src: verboliaScriptSrc,
            async: true
          }]
          : []),
        {
          src: 'https://integrations.etrusted.com/applications/widget.js/v2',
          defer: true,
          async: true
        },
        {
          type: 'application/ld+json',
          json: breadcrumbsStructuredData
        }
      ]
    };
  },
  methods: {
    mediaUrl(path): string {
      if (!path || path === '/media//') return '';
      return `${this.$config.mediaBaseURL}${path}`;
    },
    formatGalleryContent(productImageGallery) {
      let content: ImageGalleryContent = [];
      if (productImageGallery) {
        content = productImageGallery
          .filter((item) => item.media_url)
          .map((item) => {
            if (item.type === 'image') {
              return {
                ...item,
                media_url: this.mediaUrl(item.media_url)
              };
            }

            return item;
          });
      }
      if (!content.length)
        content = [
          {
            type: 'image',
            url_safe_name: ''
          }
        ];
      return content;
    },
    addGTM() {
      if (!this.pdpResult || !window || !process.client) return;
      this.initializedGTM = true;
      const gtmValue = {
        ecommerce: {
          currencyCode: this.$cookies.get(VSF_CURRENCY_COOKIE),
          detail: {
            products: [
              {
                name: this.pdpResult.name,
                id: this.pdpResult.sku,
                price: this.pdpResult.price,
                brand: this.pdpResult.brand,
                category: this.pdpResult.category,
                quantity: 1
              }
            ]
          }
        },
        Exbtw: {
          ecommerce: {
            currencyCode: this.$cookies.get(VSF_CURRENCY_COOKIE),
            detail: {
              products: [
                {
                  name: this.pdpResult.name,
                  id: this.pdpResult.sku,
                  price:
                    Math.ceil(this.pdpResult.fixed_price_excl_vat * 100) / 100,
                  brand: this.pdpResult.brand,
                  category: this.pdpResult.category,
                  quantity: 1
                }
              ]
            }
          }
        }
      };
      if (window && (window as any).dataLayer)
        (window as any).dataLayer.push({ event: 'productDetail', ...gtmValue });
    }
  }
});
